import React, { useState } from 'react'

// MUI Imports
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'

// Component Imports
import CustomTextField from '../../../component/custom_text_field/CustomTextField'

// Third-party Imports
import { Icon } from '@iconify/react/dist/iconify.js'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import * as z from 'zod'

// Hook Imports
import { useUser } from '../../../hooks/useUser'

// API Imports
import { loginReq } from '../../../api/public_api'

// Styled Component Imports
import AuthIllustrationWrapper from '../../../component/shared/AuthIllustrationWrapper/AuthIllustrationWrapper'

const schema = z.object({
    username: z.string().min(1, { message: 'This field is required..' }),
    password: z.string().min(6, { message: 'This field is required.' })
})

const Login = () => {
    // States
    const [isPasswordShown, setIsPasswordShown] = useState(false)
    const [errorState, setErrorState] = useState(null)
    const [loading, setLoading] = useState(false)

    const { updateUser } = useUser()

    const {
        control,
        handleSubmit,
        formState: { errors }
    } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            username: '',
            password: ''
        }
    })

    const onSubmit = async data => {
        try {
            setLoading(true)
            console.log(data)
            let loginRes = await loginReq({ username: data.username, password: data.password })
            localStorage.setItem('access_token', loginRes.access_token)
            localStorage.setItem('refresh_token', loginRes.refresh_token)
            updateUser({ ...loginRes, isLogin: true })
            setLoading(false)
        } catch (err) {
            setLoading(false)
        }
    }

    return (
        <div className="flex justify-center items-center mt-12 sm:mt-48">
            <AuthIllustrationWrapper>
                <Card className="flex flex-col sm:is-[450px] mx-4">
                    <CardContent>
                        <div className="text-center">
                            <Typography className="!font-bold" variant="h5">
                                OTP Profit Admin Panel
                            </Typography>
                        </div>
                    </CardContent>
                    <Divider />
                    <CardContent className="sm:!p-12">
                        <form
                            noValidate
                            autoComplete="off"
                            className="flex flex-col gap-6"
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <Controller
                                name="username"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <CustomTextField
                                        {...field}
                                        fullWidth
                                        id="username"
                                        type="username"
                                        label="Username"
                                        placeholder="Enter your username"
                                        onChange={e => {
                                            field.onChange(e.target.value)
                                            errorState !== null && setErrorState(null)
                                        }}
                                        {...((errors.username || errorState !== null) && {
                                            error: true,
                                            helperText: errors?.username?.message || errorState?.message[0]
                                        })}
                                    />
                                )}
                            />
                            <Controller
                                name="password"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <CustomTextField
                                        {...field}
                                        fullWidth
                                        label="Password"
                                        placeholder="············"
                                        id="login-password"
                                        type={isPasswordShown ? 'text' : 'password'}
                                        onChange={e => {
                                            field.onChange(e.target.value)
                                            errorState !== null && setErrorState(null)
                                        }}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => setIsPasswordShown(show => !show)}
                                                        onMouseDown={e => e.preventDefault()}
                                                    >
                                                        <Icon icon={isPasswordShown ? 'mdi:eye' : 'mdi:eye-off'} />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                        {...(errors.password && { error: true, helperText: errors.password.message })}
                                    />
                                )}
                            />
                            <Button fullWidth variant="contained" type="submit" disabled={loading}>
                                {loading && <CircularProgress className="mr-4" size={20} color="inherit" />}
                                Sign In
                            </Button>
                        </form>
                    </CardContent>
                </Card>
            </AuthIllustrationWrapper>
        </div>
    )
}

export default Login
